import { ReloadOutlined } from "@ant-design/icons";
import Auth from "@aws-amplify/auth";
import { Layout } from "antd";
import "antd/dist/antd.css";
import { useCallback, useContext, useState } from "react";
import { useTranslation } from "react-i18next";
import { BrowserRouter, Redirect, Route, Switch } from "react-router-dom";
import { useMyUser } from "./api";
import "./App.css";
import { Environment } from "./components/Environment";
import { NavigationMenu } from "./components/NavigationMenu";
import { UserMenu } from "./components/user/UserMenu";
import { AccessToken, AuthContext, CurrentUser } from "./contexts/authContext";
import { DataConsumerProvider } from "./contexts/dataConsumerContext";
import {
  DataAutoRefreshContext,
  DataRefreshContext,
} from "./contexts/dataRefreshContext";
import { routes } from "./routes";
import { Login } from "./screens/login/Login";
import { NoOrganization } from "./screens/no-role/NoRole";
import { useSetting } from "./util";
import { FeatureFlagProvider } from "./contexts/featureFlagContext";
import { useActiveUserRoles } from "./util/useActiveUserRoles";

function VerifyRoadSafetyManagerHasAnOrganization(props: {
  children: React.ReactNode;
}) {
  const { isRoadSafetyManager } = useActiveUserRoles();
  const authContext = useContext(AuthContext);
  const userId = authContext.currentUser?.accessToken.payload.sub || "";
  const myUser = useMyUser(userId);

  return (
    <>
      {isRoadSafetyManager && !myUser.loading && !myUser.value?.org ? (
        <NoOrganization />
      ) : (
        props.children
      )}
    </>
  );
}

function App() {
  const { t } = useTranslation();
  const [user, setUser] = useState<CurrentUser | undefined>(undefined);
  const [refreshCounter, setRefreshCounter] = useState(1);

  const [autoRefreshInterval, setAutoRefreshInterval] = useSetting(
    "auto-refresh-interval",
    60000
  );
  const [autoRefreshDisabled, setAutoRefreshDisabled] = useState(false);
  const enableAutoRefresh = useCallback(
    () => setAutoRefreshDisabled(false),
    [setAutoRefreshDisabled]
  );
  const disableAutoRefresh = useCallback(
    () => setAutoRefreshDisabled(true),
    [setAutoRefreshDisabled]
  );

  const handleAuthStateChange = async (a: any, c?: any) => {
    if (c?.attributes?.email) {
      const session = await Auth.currentSession();
      const accessToken = new AccessToken(session.getAccessToken());

      setUser({
        email: c?.attributes?.email,
        accessToken,
      });
    } else {
      setUser(undefined);
    }
  };

  const onRefresh = (againWithDelay?: boolean) => {
    setRefreshCounter((c) => c + 1);
    if (againWithDelay) {
      setTimeout(() => setRefreshCounter((c) => c + 1), 2000);
    }
  };

  const onRefreshClick = () => {
    onRefresh();
  };

  const signOut = async () => {
    await Auth.signOut();
    setUser(undefined);
  };

  return (
    <Environment>
      <Login handleAuthStateChange={handleAuthStateChange} currentUser={user}>
        <AuthContext.Provider
          value={{
            currentUser: user,
            signOut: signOut,
          }}
        >
          <DataRefreshContext.Provider
            value={{ refreshCounter, refresh: onRefresh }}
          >
            <DataAutoRefreshContext.Provider
              value={{
                interval: autoRefreshInterval,
                setInterval: setAutoRefreshInterval,
                disabled: autoRefreshDisabled,
                disable: disableAutoRefresh,
                enable: enableAutoRefresh,
              }}
            >
              {!!user && (
                <VerifyRoadSafetyManagerHasAnOrganization>
                  <DataConsumerProvider userId={user.accessToken.payload.sub}>
                    <FeatureFlagProvider>
                      <BrowserRouter>
                        <Layout style={{ height: "100vh" }}>
                          <Layout.Header>
                            <div className="app-cm1-logo"></div>
                            <div
                              style={{
                                float: "right",
                                marginRight: -20,
                                userSelect: "none",
                              }}
                            >
                              <UserMenu />
                            </div>
                            <div
                              style={{
                                float: "right",
                                marginRight: "1em",
                                transform: "translateY(4px)",
                                userSelect: "none",
                              }}
                            >
                              <ReloadOutlined
                                title={t("reload")}
                                className="clickable"
                                onClick={onRefreshClick}
                              />
                            </div>
                            <NavigationMenu />
                          </Layout.Header>
                          <Layout.Content style={{ padding: 10 }}>
                            <Switch>
                              {routes(user).map((route, index) => (
                                <Route
                                  key={index}
                                  path={route.path}
                                  exact
                                  children={<route.component />}
                                />
                              ))}
                              <Redirect
                                key="redirect"
                                to={
                                  user.accessToken.hasRole(
                                    "road-safety-manager"
                                  )
                                    ? "construction-projects"
                                    : user.accessToken.hasRole(
                                        "scb-one-data-analyst"
                                      )
                                    ? "scb-one"
                                    : user.accessToken.hasRole(
                                        "integration-platform-user"
                                      ) &&
                                      !user.accessToken.hasRole(
                                        "road-safety-manager"
                                      ) &&
                                      !user.accessToken.hasRole(
                                        "solution-operator"
                                      )
                                    ? "integrations"
                                    : "beacons"
                                }
                              />
                            </Switch>
                          </Layout.Content>
                        </Layout>
                      </BrowserRouter>
                    </FeatureFlagProvider>
                  </DataConsumerProvider>
                </VerifyRoadSafetyManagerHasAnOrganization>
              )}
            </DataAutoRefreshContext.Provider>
          </DataRefreshContext.Provider>
        </AuthContext.Provider>
      </Login>
    </Environment>
  );
}

export default App;
